import type { DefinitionOrBoolean } from 'typescript-json-schema';
import type { ConvertedMetadata } from '@sap-ux/vocabularies-types';
import type {
    ExtensionLogger,
    MacrosXMLPath,
    SchemaDefinition,
    MacrosSchemaMetadata
} from '@sap/ux-specification-types';
import { LogSeverity, MacrosAggregatioCardinality, MacrosPropertyType } from '@sap/ux-specification-types';
import { getDefinitionsRef, getDefinitionKey } from '../../../common/utils';
import { parseXML, getXmlnsDependencies } from '../../utils/macros';
import type { XMLElement } from '@xml-tools/ast';
import { macrosHandlers } from './annotations';
import type { HandlerParams } from './annotations';
import { handleExtensions } from './extensions';
import type { ExtensionFileData, PageConnection } from '../../types';
import { initI18n } from '../../../../i18n/i18n';
import i18next from 'i18next';
import { log } from '../../../../extensionLogger';
import { v4 as uuidV4 } from 'uuid';
import { getNodeByName, getViewNodeDescriptionAndIcon, addDisplayInfoForBuildingBlock } from './utils';
import { getEnumEntriesFromMap, MACROS_ENUM_ENTRIES_MAP } from '../../../../../scripts/macros/corrections';

const MACROS_NAMESPACE = 'sap.fe.macros';

interface UI5Depenencies {
    [key: string]: string;
}

interface XMLParseParams {
    macrosPrefixes: string[];
    schema: SchemaDefinition;
    dependencies: UI5Depenencies;
    index: number;
    isUnique: boolean;
    path: MacrosXMLPath;
    serviceAVT: ConvertedMetadata;
    connectionContext: PageConnection;
    logger?: ExtensionLogger;
}

interface DefaultAggregationData {
    schema: SchemaDefinition;
    name: string;
}

/**
 * Method returns full element name by concating name and namespace.
 *
 * @param {XMLElement} element Parsed XML element.
 * @returns {string} Full name of element including namespace.
 */
function getElementName(element: XMLElement): string {
    const { ns, name } = element;
    if (ns) {
        return `${ns}:${name}`;
    }
    return name;
}

/**
 * Method checks if passed XML element is defined within passed namespace.
 *
 * @param {XMLElement} element Parsed XML element.
 * @param {string[]} namespacePrefixes Used namespace prefixes.
 * @returns {boolean} Is element defined within passed namespace.
 */
function isElementWithNamespace(element: XMLElement, namespacePrefixes: string[]): boolean {
    const { ns = '' } = element;
    if (!namespacePrefixes) {
        return !ns;
    } else {
        return namespacePrefixes.includes(ns);
    }
}

/**
 * Method finds available name for properties in schema's properties.
 *
 * @param {string} name Original property name.
 * @param {{ [k: string]: SchemaDefinition }} properties Object with existing properties.
 * @returns {string} Available name in properties.
 */
function findAvailableName(name: string, properties: { [k: string]: DefinitionOrBoolean }): string {
    let availableName = name;
    let index = 2;
    while (availableName in properties) {
        availableName = `${name}${index}`;
        index++;
    }

    return availableName;
}

/**
 * Method prepares schema 'properties' for passed schema by resolving '$ref' property.
 *
 * @param {SchemaDefinition} schema Schema node to update.
 * @param {SchemaDefinition} fullSchema Full schema object which contains definitions.
 * @param {boolean} [isViewNodeOverride] Optional override for isViewNode value.
 */
function prepareSchemaProperties(
    schema: SchemaDefinition,
    fullSchema: SchemaDefinition,
    isViewNodeOverride?: boolean
): void {
    schema.properties = {};
    if (schema.$ref) {
        const key = getDefinitionKey(schema.$ref);
        const definition = fullSchema.definitions[key] as SchemaDefinition;
        if (typeof definition === 'object' && definition.properties) {
            schema.type = definition.type;
            // Use override if provided, otherwise fall back to definition's isViewNode
            schema.isViewNode = isViewNodeOverride ?? definition.isViewNode;
            schema.properties = JSON.parse(JSON.stringify(definition.properties));
            // Copy additionalProperties for aggregation schemas (needed for custom extension handling)
            if (definition.additionalProperties) {
                schema.additionalProperties = JSON.parse(JSON.stringify(definition.additionalProperties));
            }
        }
        delete schema.$ref;
    }
}

/**
 * Method handles attributes of xml node and stores range position to schema.
 *
 * @param {XMLElement} node Parsed XML element.
 * @param {SchemaDefinition} schema Schema object containing properties matching to element attributes.
 * @param {SchemaDefinition} fullSchema Full schema object which contains definitions.
 */
function handleAttributes(node: XMLElement, schema: SchemaDefinition, fullSchema: SchemaDefinition): void {
    if (!schema.properties) {
        prepareSchemaProperties(schema, fullSchema);
    }
    const properties = schema.properties as { [key: string]: SchemaDefinition };

    for (const attr of node.attributes) {
        const property = properties[attr.key];
        if (property && property.metadata) {
            property.metadata.position = attr.position;
        }
    }
    // Apply corrections in schema to property/attribute
    const controlName = schema.metadata?.controlName ?? '';
    const controlCorrectionEntry = MACROS_ENUM_ENTRIES_MAP[controlName] ?? {};
    for (const name in controlCorrectionEntry) {
        const property = properties[name];
        // Update enum entries based on dependent property value
        const dependentProperty = controlCorrectionEntry?.[name]?.dependentProperty;
        if (dependentProperty) {
            const dependencyValue = node.attributes.find((attribute) => attribute.key === dependentProperty);
            if (dependencyValue?.value) {
                property.enum = getEnumEntriesFromMap(controlName, name, dependencyValue.value);
            }
        }
    }
}

/**
 * Method adds custom ID property for macros control type in schema.
 *
 * @param {MacrosSchemaMetadata} metadata Macros metadata.
 */
function applyIdForMacros(metadata: MacrosSchemaMetadata): void {
    if (metadata.type === MacrosPropertyType.Control) {
        metadata.internalId = uuidV4();
    }
}

/**
 * Method calls annotation handler for passed macros control.
 *
 * @param {string} macrosControl Full macros control namespace. Like 'sap.fe.macros.Table'.
 * @param {string} options Handler options.
 * @param {ExtensionLogger} [logger] Logger.
 */
function callAnnotationHandler(macrosControl: string, options: HandlerParams, logger?: ExtensionLogger): void {
    try {
        macrosHandlers[macrosControl]?.(options);
    } catch {
        log(logger, {
            severity: LogSeverity.Error,
            message: i18next.t('MACROS_ANNOTATION_ERROR', { control: macrosControl })
        });
    }
}

/**
 * Method updates/syncs passed schema's metadata with metadata from passed reference.
 *
 * @param {MacrosSchemaMetadata} metadata Metadata to update/sync.
 * @param {SchemaDefinition} schema Reference schema.
 * @param {string} refName Reference schema name. In case of ui5 control schema - name is control name.
 */
function updateMetadataByReference(metadata: MacrosSchemaMetadata, schema: SchemaDefinition, refName: string): void {
    if (!metadata.type) {
        metadata.type = schema.metadata.type;
    }
    if (schema.metadata.defaultAggregation) {
        metadata.defaultAggregation = schema.metadata.defaultAggregation;
    }
    if (metadata.type === MacrosPropertyType.Control) {
        metadata.controlName = refName;
    }
}

/**
 * Builds a list of possible fully qualified reference names for a given XML element.
 *
 * @param node The XML element being processed.
 * @param name The control or reference name to append to each namespace.
 * @param params Parse context containing schema metadata, dependencies, and the path stack.
 * @returns List of possible fully qualified reference names.
 */
function getPosibleRefNames(node: XMLElement, name: string, params: XMLParseParams): string[] {
    const namespaces = params.schema.metadata.namespaces ? [...params.schema.metadata.namespaces] : [];
    const dependencies = params.dependencies;
    const preferredNamespaces: string[] = [MACROS_NAMESPACE];
    // Resolve namespace of control
    if (node.ns) {
        for (const ns in dependencies) {
            if (node.ns === dependencies[ns]) {
                preferredNamespaces.push(ns);
                break;
            }
        }
    }
    // Find context control like case when action defined within table using same general macros namespace
    for (let i = params.path.length - 1; i >= 0; i--) {
        let name = params.path[i].name;

        // if it has a namespace, take part after colon
        if (name.includes(':')) {
            name = name.split(':')[1];
        }

        // check if control name - control name starts with uppercase
        if (/^[A-Z]/.test(name)) {
            const ns = `${MACROS_NAMESPACE}.${name.toLowerCase()}`;
            if (namespaces.includes(ns)) {
                preferredNamespaces.push(ns);
                break;
            }
        }
    }

    return [...new Set([...preferredNamespaces, ...namespaces])].map((ns) => `${ns}.${name}`);
}

/**
 * Method adds control or complex property to schema's properties.
 *
 * @param {XMLElement} node Parsed XML element.
 * @param {SchemaDefinition} schema Schema object to update with new property.
 * @param {XMLParseParams} params XML parsing params.
 * @param {string} name Property name.
 * @returns {string} Added property name.
 */
function addControlToSchema(node: XMLElement, schema: SchemaDefinition, params: XMLParseParams, name: string): string {
    const fullSchema = params.schema;
    if (!schema.properties) {
        prepareSchemaProperties(schema, fullSchema);
    }
    const schemaProperty = schema.properties[name] as SchemaDefinition;
    const type = schemaProperty?.metadata?.type;
    const defaultAggregation = schemaProperty?.metadata?.defaultAggregation;
    const aggregationCardinality = schemaProperty?.metadata?.aggregationCardinality;
    const metadata: MacrosSchemaMetadata = {
        path: params.path,
        type,
        position: node.position
    };
    addDisplayInfoForBuildingBlock(node, name, metadata);
    if (defaultAggregation) {
        metadata.defaultAggregation = defaultAggregation;
    }
    if (aggregationCardinality === 'Single') {
        metadata.aggregationCardinality = aggregationCardinality;
    }
    const objectKey = schemaProperty?.metadata?.path?.length ? findAvailableName(name, schema.properties) : name;
    let refs: string[] = [];
    if (schemaProperty?.$ref) {
        refs.push(getDefinitionKey(schemaProperty.$ref));
    } else {
        refs = getPosibleRefNames(node, name, params);
    }
    let handled = false;
    for (const ref of refs) {
        const refDefinition = fullSchema.definitions?.[ref] as SchemaDefinition;
        if (typeof refDefinition !== 'object') {
            continue;
        }
        updateMetadataByReference(metadata, refDefinition, ref);
        const { description, icon } = getViewNodeDescriptionAndIcon(node, name);
        const newDefinition: SchemaDefinition = {
            $ref: getDefinitionsRef(ref),
            description,
            ...(icon ? { icon } : {}),
            propertyIndex: params.index,
            metadata
        };
        handleAttributes(node, newDefinition, fullSchema);
        schema.properties[objectKey] = newDefinition;
        handled = true;
        // Call annotation handler
        callAnnotationHandler(
            ref,
            {
                ...params,
                node,
                schema: newDefinition,
                rootSchema: params.schema
            },
            params.logger
        );

        break;
    }
    applyIdForMacros(metadata);
    if (!handled) {
        if (!schemaProperty?.properties) {
            // Unknown definition
            const newDefinition: SchemaDefinition = {
                properties: {},
                isViewNode: metadata.aggregationCardinality !== MacrosAggregatioCardinality.Single ? true : false,
                type: 'object',
                description: schemaProperty?.description ?? name,
                propertyIndex: params.index,
                metadata
            };
            schema.properties[objectKey] = newDefinition;
        } else {
            schemaProperty.metadata = metadata;
        }
    }

    return objectKey;
}

/**
 * Method helps to populate used definition keys by looking in passed schema properties.
 *
 * @param {SchemaDefinition} schema Schema to check for properties.
 * @param {string[] | undefined} usedDefinitionKeys Parameter populates used definition keys in macros schema.
 */
function populateUsedDefinitionKeys(schema: SchemaDefinition, usedDefinitionKeys: string[]) {
    const properties = schema.properties;
    if (properties) {
        for (const i in properties) {
            const property = properties[i] as SchemaDefinition;
            const refs: string[] = [];
            if (property.$ref) {
                refs.push(property.$ref);
            }
            if (typeof property.additionalProperties === 'object' && property.additionalProperties?.$ref) {
                refs.push(property.additionalProperties.$ref);
            }
            for (const ref of refs) {
                const definitionKey = getDefinitionKey(ref);
                if (definitionKey && usedDefinitionKeys.indexOf(definitionKey) === -1) {
                    usedDefinitionKeys.push(definitionKey);
                }
            }
            populateUsedDefinitionKeys(property, usedDefinitionKeys);
        }
    }
}

/**
 * Mapping of interface definitions to their implementing classes.
 * Used to resolve interface $refs to concrete implementations for element name matching.
 */
const INTERFACE_TO_IMPLEMENTATION_MAP: Map<string, string[]> = new Map([
    ['sap.fe.macros.table.ITableColumn', ['Column', 'ColumnOverride']],
    ['sap.fe.macros.table.ITableActionOrGroup', ['Action', 'ActionOverride', 'ActionGroup', 'ActionGroupOverride']]
]);

/**
 * Method checks if passed node is addable to passed schema by checking "additionalProperties".
 *
 * @param {XMLElement} node XML element.
 * @param {DefinitionOrBoolean} [schema] JSON schema.
 * @returns {boolean} True if node is addable for schema's additionalProperties.
 */
function isElementAddableToSchema(node: XMLElement, schema?: DefinitionOrBoolean): boolean {
    if (
        typeof schema === 'object' &&
        typeof schema.additionalProperties === 'object' &&
        schema.additionalProperties.$ref
    ) {
        const ref = schema.additionalProperties.$ref;
        const definitionKey = getDefinitionKey(ref);
        const parts = definitionKey.split('.');
        // `sap.ui.core.Control` allows to insert any control
        if (definitionKey === 'sap.ui.core.Control' || parts[parts.length - 1] === node.name) {
            return true;
        }
    }
    return false;
}

/**
 * Method returns default aggregation information if schema contains default aggregation.
 *
 * @param {XMLElement} node Parsed XML element.
 * @param {SchemaDefinition} schema Schema object to check for default aggregation.
 * @returns {DefaultAggregationData} Default aggregation information.
 */
function getDefaultAggregation(node: XMLElement, schema: SchemaDefinition): DefaultAggregationData | undefined {
    const defaultAggregation = schema.metadata?.defaultAggregation;
    if (defaultAggregation && !getNodeByName(node, defaultAggregation)) {
        const defaultAggregationSchema = schema.properties[defaultAggregation];
        if (typeof defaultAggregationSchema === 'object') {
            return {
                schema: defaultAggregationSchema,
                name: defaultAggregation
            };
        }
    }
    return undefined;
}

/**
 * Handles subelements of an XML node.
 *
 * @param {XMLElement[]} subElements of a parent node.
 * @param {SchemaDefinition} schema Schema object to update with new property.
 * @param {XMLParseParams} params XML parsing params.
 * @param {string[] | undefined} usedDefinitionKeys Parameter populates used definition keys in macros schema.
 */
function handleSubElementsOfXMLNode(
    subElements: XMLElement[],
    schema: SchemaDefinition,
    params: XMLParseParams,
    usedDefinitionKeys: string[]
) {
    const uniqueKeys: { [key: string]: boolean } = {};

    for (let index = 0; index < subElements.length; index++) {
        const element = subElements[index];
        const isUnique = !uniqueKeys[element.name];
        uniqueKeys[element.name] = true;
        handleXMLNode(
            element,
            schema,
            {
                ...params,
                isUnique,
                index,
                path: [
                    ...params.path,
                    {
                        name: getElementName(element),
                        index
                    }
                ]
            },
            usedDefinitionKeys
        );
    }
}

/**
 * Method adds property to schema's properties.
 *
 * @param {XMLElement} node Parsed XML element.
 * @param {SchemaDefinition} schema Schema object to update with new property.
 * @param {XMLParseParams} params XML parsing params.
 * @param {string[] | undefined} usedDefinitionKeys Parameter populates used definition keys in macros schema.
 */
function handleXMLNode(
    node: XMLElement,
    schema: SchemaDefinition,
    params: XMLParseParams,
    usedDefinitionKeys?: string[]
): void {
    const postProcesses: Array<{ node: XMLElement; schema: SchemaDefinition; name?: string }> = [];
    const { macrosPrefixes } = params;
    const parentSchema = schema?.$ref ? params.schema.definitions?.[getDefinitionKey(schema.$ref)] : schema;
    if (
        isElementWithNamespace(node, macrosPrefixes) ||
        (typeof parentSchema === 'object' && parentSchema.properties?.[node.name])
    ) {
        const defaultAggregation = schema.metadata?.defaultAggregation;
        const defaultAggSchema = defaultAggregation
            ? (schema.properties?.[defaultAggregation] as SchemaDefinition | undefined)
            : undefined;
        if (defaultAggregation && isElementAddableToSchema(node, defaultAggSchema)) {
            schema = defaultAggSchema!;
        }
        // If this element is a ColumnOverride/ActionOverride for an existing annotation column, attach
        // attribute positions to the override target's schema, then skip creating a separate custom node.
        // Also checks the default aggregation schema to handle ColumnOverride placed as a direct child
        // of the parent control (default aggregation, no explicit wrapper element).
        const overrideKey = node.attributes.find((a) => a.key === 'key')?.value;
        const overrideTarget = overrideKey
            ? ((schema.properties?.[overrideKey] as SchemaDefinition | undefined) ??
              (defaultAggSchema?.properties?.[overrideKey] as SchemaDefinition | undefined))
            : undefined;
        if (overrideKey && overrideTarget?.metadata?.overrideClass) {
            handleAttributes(node, overrideTarget, params.schema);
            return;
        }
        const names = node.name.split(':');
        const name = addControlToSchema(node, schema, params, names[names.length - 1]);
        // Go deeper in schema
        if (!schema.properties[name]) {
            return;
        } else {
            schema = schema.properties[name] as SchemaDefinition;
        }
    }
    const { subElements = [] } = node;
    const definitionName = schema?.metadata ? schema.metadata.controlName : undefined;
    if (
        usedDefinitionKeys &&
        definitionName &&
        params.schema.definitions[definitionName] &&
        usedDefinitionKeys.indexOf(definitionName) === -1
    ) {
        usedDefinitionKeys.push(definitionName);
        // search deeper for macros definitions
        populateUsedDefinitionKeys(schema, usedDefinitionKeys);
    }
    const defaultAggregation = getDefaultAggregation(node, schema);
    if (defaultAggregation) {
        postProcesses.push({ node, schema: defaultAggregation.schema, name: defaultAggregation.name });
    }
    handleSubElementsOfXMLNode(subElements, schema, params, usedDefinitionKeys);
    postProcesses.push({ node, schema });
    // Post process schema
    for (const postProcess of postProcesses) {
        handleExtensions(postProcess.node, postProcess.schema, params.schema, postProcess.name);
    }
}

/**
 * Method returns available macros namespace prefixes.
 *
 * @param {SchemaDefinition} genericSchema Generic schema of "sap.fe.macros".
 * @param {UI5Depenencies} dependencies Used dependencies.
 * @returns {string[]} Available macros namespace prefixes.
 */
function getMacrosPrefixes(genericSchema: SchemaDefinition, dependencies: UI5Depenencies): string[] {
    const macrosPrefixes = [];
    // populate available namespace prefixes
    if (genericSchema?.metadata?.namespaces) {
        genericSchema.metadata.namespaces.forEach((prefix: string) => {
            if (prefix in dependencies) {
                macrosPrefixes.push(dependencies[prefix]);
            }
        });
    }
    return macrosPrefixes;
}

/**
 * Method generates schema for passed XML content.
 *
 * @param {SchemaDefinition} genericSchema Generic schema of "sap.fe.macros".
 * @param {ExtensionFileData} xmlFile Object with xml file.
 * @param {ConvertedMetadata} serviceAVT OData metadata.
 * @param {ExtensionLogger} [logger] - Logger class for logging messages.
 * @returns {SchemaDefinition} Generated schema for passed XML content.
 */
export function generateMacrosSchemaDefinition(
    genericSchema: SchemaDefinition,
    xmlFile: ExtensionFileData,
    serviceAVT: ConvertedMetadata,
    logger?: ExtensionLogger
): SchemaDefinition | undefined {
    const viewDocument = parseXML(xmlFile.file, true, logger);
    const usedDefinitionKeys: string[] = [];
    if (!viewDocument) {
        return undefined;
    }
    initI18n();
    // define default schema without any definitions and properties
    let schema: SchemaDefinition = {
        type: 'object',
        properties: {},
        definitions: {},
        additionalProperties: false,
        $schema: 'http://json-schema.org/draft-07/schema#'
    };
    if (!genericSchema) {
        log(logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOGENERICSCHEMA', { pageType: 'FPM Custom Page' })
        });
        return;
    }
    const coreElement = viewDocument.rootElement;
    const dependencies = getXmlnsDependencies((coreElement.attributes || []).map((a) => [a.key, a.value]));
    const macrosPrefixes = getMacrosPrefixes(genericSchema, dependencies);
    if (macrosPrefixes.length) {
        // Macros supported
        schema = JSON.parse(JSON.stringify(genericSchema));
        const uniqueKeys: { [key: string]: boolean } = {};
        for (let index = 0; index < coreElement.subElements.length; index++) {
            const element = coreElement.subElements[index];
            const isUnique = !uniqueKeys[element.name];
            uniqueKeys[element.name] = true;
            handleXMLNode(
                element,
                schema,
                {
                    macrosPrefixes,
                    schema,
                    dependencies,
                    index,
                    isUnique,
                    path: [
                        {
                            name: getElementName(element),
                            index
                        }
                    ],
                    serviceAVT,
                    connectionContext: xmlFile.connection,
                    logger
                },
                usedDefinitionKeys
            );
        }
        // clean unused definitions from macros schema
        for (const i in schema.definitions) {
            if (schema.definitions[i]['metadata'] && !usedDefinitionKeys.includes(i)) {
                delete schema.definitions[i];
            }
        }
    }
    return schema;
}
